import React, { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Carousel as BootstrapCarousel } from 'bootstrap';
import { Container } from '@components/atoms/Container';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { useThemes } from '@hooks/useThemes';
import { FinancingLogo } from '../FinancingLogo';
import { HeroSection } from '../HeroSection';
import styles from './Carousel.module.css';

export interface CarouselProps {
    items: {
        theme?: 'dark' | 'light';
        desktopImg?: { src?: string; type?: string; width?: number; height?: number };
        mobileImg?: { src?: string; type?: string; width?: number; height?: number };
        category: string;
        title: string;
        subtitle?: string;
        description?: string;
        link: { label: string; url: string };
        footerNote?: string;
    }[];
    mode: 'boxed' | 'wide';
    includeLogos: boolean;
}

export const Carousel: FC<CarouselProps> = ({ items, mode, includeLogos }) => {
    const { t } = useTranslation();

    const translations = {
        previous: t('carousel-component.previous'),
        next: t('carousel-component.next'),
    };

    const [carousel, setCarousel] = useState<BootstrapCarousel | null>(null);

    const carouselRef = useCallback((node: HTMLDivElement) => {
        if (node !== null) {
            const carouselInstance = BootstrapCarousel.getOrCreateInstance(node, { ride: 'carousel' });
            setCarousel(carouselInstance);
        }
    }, []);

    return (
        <Container classes={mode === 'boxed' ? 'px-3' : 'px-0'} type={mode === 'boxed' ? 'default' : 'fluid'}>
            <div ref={carouselRef} className={`carousel ${styles.carousel} slide`} data-bs-ride="carousel" data-testid="carousel">
                <div className="carousel-inner">
                    {items.map((item, index) => (
                        <div key={`carousel-item-${index}`} className={`carousel-item ${index === 0 ? 'active' : ''}`}>
                            <HeroSection
                                mode={mode}
                                category={item.category}
                                title={item.title}
                                subtitle={item.subtitle}
                                description={item.description ? [item.description] : []}
                                desktopImg={item.desktopImg?.src}
                                mobileImg={item.mobileImg?.src}
                                link={{
                                    label: item.link.label,
                                    url: item.link.url,
                                }}
                                footerNote={item.footerNote}
                                theme={item.theme}
                            />
                        </div>
                    ))}
                </div>
                <Container>
                    <div className="position-relative">
                        {includeLogos && <LogosDisplay />}
                        {/* controls: previous and next */}
                        {items.length > 1 && (
                            <div className={`carousel-controls-container ${styles['carousel-controls-container']}`}>
                                <button
                                    className={`carousel-control-prev ${styles['carousel-control-prev']}`}
                                    onClick={() => carousel?.prev()}>
                                    <span
                                        className={`carousel-control-prev-icon ${styles['carousel-control-prev-icon']}`}
                                        aria-hidden="true">
                                        <Typography tagVariant={TypographyTagVariants.span}>
                                            <i className="fa-light fa-arrow-left" />
                                        </Typography>
                                    </span>
                                    <span className="visually-hidden">{translations.previous}</span>
                                </button>
                                <button
                                    className={`carousel-control-next ${styles['carousel-control-next']}`}
                                    onClick={() => carousel?.next()}>
                                    <span
                                        className={`carousel-control-next-icon ${styles['carousel-control-next-icon']}`}
                                        aria-hidden="true">
                                        <Typography tagVariant={TypographyTagVariants.span}>
                                            <i className="fa-light fa-arrow-right" />
                                        </Typography>
                                    </span>
                                    <span className="visually-hidden">{translations.next}</span>
                                </button>
                            </div>
                        )}
                    </div>
                </Container>
            </div>
        </Container>
    );
};

const LogosDisplay: FC = () => {
    const { useGrayscaleTheme } = useThemes();

    return (
        <div
            className={
                // themes
                `${useGrayscaleTheme ? styles['theme-grayscale'] : ''}` +
                // other classes
                ` ${styles['content-next-to-carousel-controls']} d-none d-lg-block align-content-center px-4`
            }>
            <FinancingLogo />
        </div>
    );
};
